// lib/AIPreferences.js
import { Client, Databases, ID, Query } from 'appwrite'
import { appwriteConfig } from './appwrite'

// Initialize Appwrite client
const client = new Client()
client
  .setEndpoint(appwriteConfig.endpoint)
  .setProject(appwriteConfig.projectId)

const databases = new Databases(client)

// Configuration constants
const AI_DATABASE_ID = appwriteConfig.aiDatabaseId
const AI_PREFERENCES_COLLECTION_ID = '684362a70000e253f7f1'

/**
 * Convert routing rules object to Appwrite string array format
 * @param {Object} routingRules - Object like {billing: 'billing', technical: 'technical'}
 * @returns {Array} Array like ['billing:billing', 'technical:technical']
 */
const convertRoutingRulesToArray = (routingRules) => {
  if (!routingRules || typeof routingRules !== 'object') return []
  return Object.entries(routingRules).map(([key, value]) => `${key}:${value}`)
}

/**
 * Parse routing rules from Appwrite string array to object
 * @param {Array} routingArray - Array like ['billing:billing', 'technical:technical']
 * @returns {Object} Object like {billing: 'billing', technical: 'technical'}
 */
const parseRoutingRulesFromArray = (routingArray) => {
  if (!Array.isArray(routingArray)) return {}
  const rules = {}
  routingArray.forEach(rule => {
    if (typeof rule === 'string' && rule.includes(':')) {
      const [key, value] = rule.split(':')
      rules[key.trim()] = value.trim()
    }
  })
  return rules
}

/**
 * Load AI preferences for a company
 * @param {string} companyId - Company ID
 * @returns {Promise<Object|null>} AI preferences object or null if not found
 */
export const loadAIPreferences = async (companyId) => {
  if (!companyId) {
    throw new Error('Company ID ist erforderlich')
  }

  try {
    console.log('📋 Lade AI-Einstellungen für Firma:', companyId)

    const response = await databases.listDocuments(
      AI_DATABASE_ID,
      AI_PREFERENCES_COLLECTION_ID,
      [
        Query.equal('companyId', companyId),
        Query.equal('isActive', true),
        Query.orderDesc('updatedAt'),
        Query.limit(1)
      ]
    )

    if (response.documents.length > 0) {
      console.log('✅ AI-Einstellungen geladen:', response.documents[0])
      
      // Parse arrays and objects from Appwrite format
      const doc = response.documents[0]
      const parsedDoc = {
        ...doc,
        // Arrays are already arrays from Appwrite - just ensure they exist
        alwaysEscalateTypes: doc.alwaysEscalateTypes || [],
        escalationKeywords: doc.escalationKeywords || [],
        commonTopics: doc.commonTopics || [],
        // Parse routing rules from string array to object
        routingRules: doc.routingRules && doc.routingRules.length > 0 ? 
          parseRoutingRulesFromArray(doc.routingRules) : {
            billing: 'billing',
            technical: 'technical',
            general: 'support',
            complaints: 'management'
          },
        // Parse businessHours for EscalationCard compatibility
        businessHours: doc.workingHours ? 
          (typeof doc.workingHours === 'string' ? 
            JSON.parse(doc.workingHours) : 
            doc.workingHours) : {
              enabled: false,
              timezone: doc.timezone || 'Europe/Zurich',
              monday: { enabled: true, start: '09:00', end: '17:00' },
              tuesday: { enabled: true, start: '09:00', end: '17:00' },
              wednesday: { enabled: true, start: '09:00', end: '17:00' },
              thursday: { enabled: true, start: '09:00', end: '17:00' },
              friday: { enabled: true, start: '09:00', end: '17:00' },
              saturday: { enabled: false, start: '09:00', end: '17:00' },
              sunday: { enabled: false, start: '09:00', end: '17:00' }
            }
      }
      
      return parsedDoc
    } else {
      console.log('⚠️ Keine AI-Einstellungen gefunden, verwende Standards')
      return null
    }
  } catch (error) {
    console.error('❌ Fehler beim Laden der AI-Einstellungen:', error)
    throw error
  }
}

/**
 * Save AI preferences for a company
 * @param {string} companyId - Company ID
 * @param {string} userId - User ID
 * @param {Object} preferences - Preferences object
 * @returns {Promise<Object>} Saved preferences document
 */
export const saveAIPreferences = async (companyId, userId, preferences) => {
  if (!companyId || !userId) {
    throw new Error('Company ID und User ID sind erforderlich')
  }

  try {
    console.log('💾 Speichere AI-Einstellungen für Firma:', companyId)

    // Prepare data for saving - use proper Appwrite arrays
    const saveData = {
      companyId: companyId,
      userId: userId,
      isActive: true,
      
      // Personality settings
      personalityStyle: preferences.personalityStyle || 'friendly',
      language: preferences.language || 'auto',
      emojiUsage: preferences.emojiUsage || 'moderate',
      responseStyle: preferences.responseStyle || 'conversational',
      
      // Custom instructions
      customInstructions: preferences.customInstructions || '',
      greetingMessage: preferences.greetingMessage || '',
      fallbackMessage: preferences.fallbackMessage || '',
      escalationMessage: preferences.escalationMessage || '',
      
      // Behavior settings
      escalationSensitivity: preferences.escalationSensitivity || 'normal',
      useKnowledgeBase: preferences.useKnowledgeBase !== false,
      confidenceThreshold: preferences.confidenceThreshold || 0.7,
      maxResponseLength: preferences.maxResponseLength || 500,
      
      // NEW: Escalation settings - use proper Appwrite arrays
      maxConversationLength: preferences.maxConversationLength || 10,
      alwaysEscalateTypes: preferences.alwaysEscalateTypes || [],
      escalationKeywords: preferences.escalationKeywords || [],
      routingRules: preferences.routingRules ? 
        convertRoutingRulesToArray(preferences.routingRules) : [
          'billing:billing',
          'technical:technical', 
          'general:support',
          'complaints:management'
        ],
      
      // NEW: Response behavior settings
      responseDelay: preferences.responseDelay || 'natural',
      knowledgePriority: preferences.knowledgePriority || 'balanced',
      followUpQuestions: preferences.followUpQuestions !== false,
      proactiveHelp: preferences.proactiveHelp !== false,
      contextAwareness: preferences.contextAwareness !== false,
      typingIndicators: preferences.typingIndicators !== false,
      
      // Business context
      businessType: preferences.businessType || '',
      businessDescription: preferences.businessDescription || '',
      commonTopics: preferences.commonTopics || [],
      
      // Advanced settings - handle businessHours object from EscalationCard
      workingHours: preferences.businessHours ? 
        JSON.stringify(preferences.businessHours) : 
        (preferences.workingHours || JSON.stringify({ start: '09:00', end: '17:00' })),
      timezone: preferences.businessHours?.timezone || preferences.timezone || 'Europe/Zurich',
      allowOffHoursEscalation: preferences.businessHours?.allowOffHoursEscalation !== false || preferences.allowOffHoursEscalation !== false,
      
      // Metadata
      updatedAt: new Date().toISOString(),
      version: (preferences.version || 0) + 1
    }

    // Check if preferences already exist
    const existingResponse = await databases.listDocuments(
      AI_DATABASE_ID,
      AI_PREFERENCES_COLLECTION_ID,
      [
        Query.equal('companyId', companyId),
        Query.equal('isActive', true)
      ]
    )

    let savedDoc

    if (existingResponse.documents.length > 0) {
      // Update existing preferences
      const existingDoc = existingResponse.documents[0]
      savedDoc = await databases.updateDocument(
        AI_DATABASE_ID,
        AI_PREFERENCES_COLLECTION_ID,
        existingDoc.$id,
        saveData
      )
      console.log('✅ AI-Einstellungen aktualisiert')
    } else {
      // Create new preferences
      saveData.createdAt = new Date().toISOString()
      savedDoc = await databases.createDocument(
        AI_DATABASE_ID,
        AI_PREFERENCES_COLLECTION_ID,
        ID.unique(),
        saveData
      )
      console.log('✅ AI-Einstellungen erstellt')
    }

    return savedDoc
  } catch (error) {
    console.error('❌ Fehler beim Speichern der AI-Einstellungen:', error)
    throw error
  }
}

/**
 * Get default AI preferences
 * @returns {Object} Default preferences object
 */
export const getDefaultAIPreferences = () => {
  return {
    // Personality settings
    personalityStyle: 'friendly',
    language: 'auto',
    emojiUsage: 'moderate',
    responseStyle: 'conversational',
    
    // Custom instructions
    customInstructions: '',
    greetingMessage: 'Hallo! Ich bin hier, um Ihnen zu helfen. Womit kann ich Ihnen behilflich sein? 😊',
    fallbackMessage: 'Entschuldigung, ich hatte technische Schwierigkeiten. Lassen Sie mich Sie mit einem unserer Mitarbeiter verbinden!',
    escalationMessage: 'Ich leite Sie gerne an einen unserer Spezialisten weiter, der Ihnen besser helfen kann.',
    
    // Behavior settings
    escalationSensitivity: 'normal',
    useKnowledgeBase: true,
    confidenceThreshold: 0.7,
    maxResponseLength: 500,
    
    // NEW: Escalation settings
    maxConversationLength: 10,
    alwaysEscalateTypes: [],
    escalationKeywords: [],
    routingRules: {
      billing: 'billing',
      technical: 'technical',
      general: 'support',
      complaints: 'management'
    },
    
    // NEW: Response behavior settings
    responseDelay: 'natural',
    knowledgePriority: 'balanced',
    followUpQuestions: true,
    proactiveHelp: true,
    contextAwareness: true,
    typingIndicators: true,
    
    // Business context
    businessType: '',
    businessDescription: '',
    commonTopics: [],
    
    // Advanced settings
    workingHours: JSON.stringify({ start: '09:00', end: '17:00' }),
    timezone: 'Europe/Zurich',
    allowOffHoursEscalation: true
  }
}

/**
 * Validate AI preferences object
 * @param {Object} preferences - Preferences to validate
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export const validateAIPreferences = (preferences) => {
  const errors = []
  
  // Validate personality style
  const validPersonalityStyles = ['professional', 'friendly', 'casual', 'formal']
  if (preferences.personalityStyle && !validPersonalityStyles.includes(preferences.personalityStyle)) {
    errors.push('Ungültiger Persönlichkeitsstil')
  }
  
  // Validate language
  const validLanguages = ['auto', 'german', 'english']
  if (preferences.language && !validLanguages.includes(preferences.language)) {
    errors.push('Ungültige Sprache')
  }
  
  // Validate emoji usage
  const validEmojiUsage = ['none', 'minimal', 'moderate', 'high']
  if (preferences.emojiUsage && !validEmojiUsage.includes(preferences.emojiUsage)) {
    errors.push('Ungültige Emoji-Verwendung')
  }
  
  // Validate response style
  const validResponseStyles = ['concise', 'detailed', 'conversational']
  if (preferences.responseStyle && !validResponseStyles.includes(preferences.responseStyle)) {
    errors.push('Ungültiger Antwort-Stil')
  }
  
  // Validate custom instructions length
  if (preferences.customInstructions && preferences.customInstructions.length > 500) {
    errors.push('Benutzerdefinierte Anweisungen sind zu lang (max. 500 Zeichen)')
  }
  
  // Validate confidence threshold
  if (preferences.confidenceThreshold && (preferences.confidenceThreshold < 0 || preferences.confidenceThreshold > 1)) {
    errors.push('Vertrauensschwelle muss zwischen 0 und 1 liegen')
  }
  
  // Validate max response length
  if (preferences.maxResponseLength && (preferences.maxResponseLength < 50 || preferences.maxResponseLength > 2000)) {
    errors.push('Maximale Antwortlänge muss zwischen 50 und 2000 Zeichen liegen')
  }
  
  // NEW: Validate escalation settings
  if (preferences.maxConversationLength && (preferences.maxConversationLength < 3 || preferences.maxConversationLength > 50)) {
    errors.push('Maximale Gesprächslänge muss zwischen 3 und 50 Nachrichten liegen')
  }
  
  return {
    isValid: errors.length === 0,
    errors: errors
  }
}

/**
 * Delete AI preferences for a company (sets isActive to false)
 * @param {string} companyId - Company ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteAIPreferences = async (companyId) => {
  if (!companyId) {
    throw new Error('Company ID ist erforderlich')
  }

  try {
    console.log('🗑️ Deaktiviere AI-Einstellungen für Firma:', companyId)

    const response = await databases.listDocuments(
      AI_DATABASE_ID,
      AI_PREFERENCES_COLLECTION_ID,
      [
        Query.equal('companyId', companyId),
        Query.equal('isActive', true)
      ]
    )

    if (response.documents.length > 0) {
      const doc = response.documents[0]
      await databases.updateDocument(
        AI_DATABASE_ID,
        AI_PREFERENCES_COLLECTION_ID,
        doc.$id,
        {
          isActive: false,
          updatedAt: new Date().toISOString()
        }
      )
      console.log('✅ AI-Einstellungen deaktiviert')
      return true
    } else {
      console.log('⚠️ Keine aktiven AI-Einstellungen gefunden')
      return false
    }
  } catch (error) {
    console.error('❌ Fehler beim Deaktivieren der AI-Einstellungen:', error)
    throw error
  }
}

/**
 * Export AI preferences to JSON
 * @param {string} companyId - Company ID
 * @returns {Promise<string>} JSON string of preferences
 */
export const exportAIPreferences = async (companyId) => {
  try {
    const preferences = await loadAIPreferences(companyId)
    if (!preferences) {
      throw new Error('Keine AI-Einstellungen zum Exportieren gefunden')
    }
    
    // Remove internal fields for export
    const exportData = { ...preferences }
    delete exportData.$id
    delete exportData.$createdAt
    delete exportData.$updatedAt
    delete exportData.$permissions
    delete exportData.$collectionId
    delete exportData.$databaseId
    
    return JSON.stringify(exportData, null, 2)
  } catch (error) {
    console.error('❌ Fehler beim Exportieren der AI-Einstellungen:', error)
    throw error
  }
}

/**
 * Import AI preferences from JSON
 * @param {string} companyId - Company ID
 * @param {string} userId - User ID
 * @param {string} jsonData - JSON string of preferences
 * @returns {Promise<Object>} Imported preferences document
 */
export const importAIPreferences = async (companyId, userId, jsonData) => {
  try {
    const preferences = JSON.parse(jsonData)
    
    // Validate imported preferences
    const validation = validateAIPreferences(preferences)
    if (!validation.isValid) {
      throw new Error('Ungültige Einstellungen: ' + validation.errors.join(', '))
    }
    
    // Save imported preferences
    return await saveAIPreferences(companyId, userId, preferences)
  } catch (error) {
    console.error('❌ Fehler beim Importieren der AI-Einstellungen:', error)
    throw error
  }
}

/**
 * Check if AI preferences exist for a company
 * @param {string} companyId - Company ID
 * @returns {Promise<boolean>} True if preferences exist
 */
export const hasAIPreferences = async (companyId) => {
  if (!companyId) return false

  try {
    const response = await databases.listDocuments(
      AI_DATABASE_ID,
      AI_PREFERENCES_COLLECTION_ID,
      [
        Query.equal('companyId', companyId),
        Query.equal('isActive', true),
        Query.limit(1)
      ]
    )

    return response.documents.length > 0
  } catch (error) {
    console.error('❌ Fehler beim Prüfen der AI-Einstellungen:', error)
    return false
  }
}

/**
 * Get AI preferences with fallback to defaults
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} AI preferences (saved or default)
 */
export const getAIPreferencesWithDefaults = async (companyId) => {
  try {
    const preferences = await loadAIPreferences(companyId)
    
    if (preferences) {
      // Merge with defaults to ensure all fields are present
      const defaults = getDefaultAIPreferences()
      return { ...defaults, ...preferences }
    } else {
      // Return defaults
      return getDefaultAIPreferences()
    }
  } catch (error) {
    console.error('❌ Fehler beim Laden der AI-Einstellungen, verwende Standards:', error)
    return getDefaultAIPreferences()
  }
}

/**
 * Update specific preference field
 * @param {string} companyId - Company ID
 * @param {string} userId - User ID
 * @param {string} field - Field name to update
 * @param {any} value - New value
 * @returns {Promise<Object>} Updated preferences document
 */
export const updateAIPreferenceField = async (companyId, userId, field, value) => {
  try {
    // Load current preferences
    const currentPrefs = await getAIPreferencesWithDefaults(companyId)
    
    // Update the specific field
    const updatedPrefs = { ...currentPrefs, [field]: value }
    
    // Save the updated preferences
    return await saveAIPreferences(companyId, userId, updatedPrefs)
  } catch (error) {
    console.error(`❌ Fehler beim Aktualisieren des Feldes ${field}:`, error)
    throw error
  }
}

// =============================================================================
// UTILITY FUNCTIONS FOR FORMATTING AND DISPLAY
// =============================================================================

export const formatPersonalityStyle = (style) => {
  const styles = {
    professional: 'Professionell',
    friendly: 'Freundlich',
    casual: 'Locker',
    formal: 'Förmlich'
  }
  return styles[style] || style
}

export const formatLanguage = (language) => {
  const languages = {
    auto: 'Automatisch erkennen',
    german: 'Deutsch',
    english: 'Englisch'
  }
  return languages[language] || language
}

export const formatEmojiUsage = (usage) => {
  const usages = {
    none: 'Keine',
    minimal: 'Minimal',
    moderate: 'Moderat',
    high: 'Häufig'
  }
  return usages[usage] || usage
}

export const formatResponseStyle = (style) => {
  const styles = {
    concise: 'Prägnant',
    detailed: 'Detailliert',
    conversational: 'Gesprächig'
  }
  return styles[style] || style
}

export const formatEscalationSensitivity = (sensitivity) => {
  const sensitivities = {
    low: 'Niedrig',
    normal: 'Normal',
    high: 'Hoch'
  }
  return sensitivities[sensitivity] || sensitivity
}

/**
 * Get preferences summary for display
 * @param {Object} preferences - Preferences object
 * @returns {Object} Formatted summary
 */
export const getPreferencesSummary = (preferences) => {
  if (!preferences) return null

  return {
    personality: formatPersonalityStyle(preferences.personalityStyle),
    language: formatLanguage(preferences.language),
    emojiUsage: formatEmojiUsage(preferences.emojiUsage),
    responseStyle: formatResponseStyle(preferences.responseStyle),
    hasCustomInstructions: !!(preferences.customInstructions && preferences.customInstructions.trim()),
    escalationSensitivity: formatEscalationSensitivity(preferences.escalationSensitivity),
    useKnowledgeBase: preferences.useKnowledgeBase,
    lastUpdated: preferences.updatedAt ? new Date(preferences.updatedAt).toLocaleDateString('de-DE') : null
  }
}

// =============================================================================
// ESCALATION-SPECIFIC HELPER FUNCTIONS
// =============================================================================

/**
 * Get escalation settings with defaults
 * @param {Object} preferences - Full preferences object
 * @returns {Object} Escalation settings
 */
export const getEscalationSettings = (preferences) => {
  if (!preferences) return getDefaultEscalationSettings()
  
  return {
    confidenceThreshold: preferences.confidenceThreshold || 0.7,
    maxConversationLength: preferences.maxConversationLength || 10,
    alwaysEscalateTypes: preferences.alwaysEscalateTypes || [],
    escalationKeywords: preferences.escalationKeywords || [],
    routingRules: preferences.routingRules || {
      billing: 'billing',
      technical: 'technical',
      general: 'support',
      complaints: 'management'
    }
  }
}

/**
 * Get default escalation settings
 * @returns {Object} Default escalation settings
 */
export const getDefaultEscalationSettings = () => {
  return {
    confidenceThreshold: 0.7,
    maxConversationLength: 10,
    alwaysEscalateTypes: [],
    escalationKeywords: [],
    routingRules: {
      billing: 'billing',
      technical: 'technical',
      general: 'support',
      complaints: 'management'
    }
  }
}

/**
 * Validate escalation settings
 * @param {Object} escalationSettings - Escalation settings to validate
 * @returns {Object} Validation result
 */
export const validateEscalationSettings = (escalationSettings) => {
  const errors = []
  
  if (escalationSettings.confidenceThreshold && (escalationSettings.confidenceThreshold < 0.1 || escalationSettings.confidenceThreshold > 0.9)) {
    errors.push('Vertrauensschwelle muss zwischen 10% und 90% liegen')
  }
  
  if (escalationSettings.maxConversationLength && (escalationSettings.maxConversationLength < 3 || escalationSettings.maxConversationLength > 50)) {
    errors.push('Maximale Gesprächslänge muss zwischen 3 und 50 Nachrichten liegen')
  }
  
  return {
    isValid: errors.length === 0,
    errors: errors
  }
}